import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Download, FileImage } from "lucide-react";

/**
 * Renders a downloadable Guest Smell Certified™ badge.
 * SVG is inline (crisp scaling, brand-accurate). Downloads as .svg or rasterized .png (2400×2400).
 */
export default function BadgeDownload({ property, tier, code, size = 220 }) {
  const svgRef = useRef(null);
  const premium = tier === "premium";
  const brandNavy = "#0A1128";
  const brandGold = "#D4AF37";
  const brandGreen = "#10B981";
  const ring = premium ? brandGold : brandGreen;
  const label = premium ? "Premium Certified" : "Certified Fresh";
  const score = property?.freshness_score ?? 0;
  const name = property?.name || "Guest Smell Certified";
  const location = [property?.city, property?.country].filter(Boolean).join(", ");

  const svgMarkup = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 600" width="${size}" height="${size}">
      <defs>
        <radialGradient id="halo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="${ring}" stop-opacity="0.18"/>
          <stop offset="100%" stop-color="${ring}" stop-opacity="0"/>
        </radialGradient>
      </defs>
      <rect width="600" height="600" fill="#FFFFFF"/>
      <circle cx="300" cy="300" r="280" fill="url(#halo)"/>
      <circle cx="300" cy="300" r="240" fill="#FFFFFF" stroke="${ring}" stroke-width="6"/>
      <circle cx="300" cy="300" r="215" fill="none" stroke="${ring}" stroke-width="1.5" stroke-dasharray="3 6"/>
      <g fill="${brandNavy}">
        <text x="300" y="150" text-anchor="middle" font-family="Georgia, 'Playfair Display', serif" font-size="22" letter-spacing="6" font-weight="500">GUEST SMELL</text>
        <text x="300" y="180" text-anchor="middle" font-family="Georgia, 'Playfair Display', serif" font-size="12" letter-spacing="8" fill="${ring}">— ${label.toUpperCase()} —</text>
      </g>
      <circle cx="300" cy="310" r="90" fill="${brandNavy}"/>
      <text x="300" y="330" text-anchor="middle" font-family="Georgia, serif" font-size="88" fill="#FFFFFF" font-weight="600">${score}</text>
      <text x="300" y="365" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" letter-spacing="4" fill="${brandGold}">/ 100</text>
      <text x="300" y="440" text-anchor="middle" font-family="Georgia, serif" font-size="24" fill="${brandNavy}">${escapeXml(name)}</text>
      <text x="300" y="465" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" letter-spacing="3" fill="#64748B">${escapeXml(location.toUpperCase())}</text>
      <text x="300" y="520" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" letter-spacing="4" fill="#94A3B8">VERIFY · ${escapeXml((code || "").toUpperCase())}</text>
      <text x="300" y="550" text-anchor="middle" font-family="Georgia, serif" font-size="10" letter-spacing="4" fill="${ring}">SEE IT · SMELL IT · BOOK IT™</text>
    </svg>
  `.trim();

  const downloadSvg = () => {
    const blob = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    triggerDownload(url, `guest-smell-certified-${sanitize(name)}.svg`);
    URL.revokeObjectURL(url);
  };

  const downloadPng = () => {
    const img = new Image();
    const svgBlob = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 2400;
      canvas.height = 2400;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, 2400, 2400);
      ctx.drawImage(img, 0, 0, 2400, 2400);
      canvas.toBlob((blob) => {
        const purl = URL.createObjectURL(blob);
        triggerDownload(purl, `guest-smell-certified-${sanitize(name)}.png`);
        URL.revokeObjectURL(purl);
        URL.revokeObjectURL(url);
      }, "image/png");
    };
    img.src = url;
  };

  return (
    <div className="flex flex-col items-center" data-testid="badge-download">
      <div
        ref={svgRef}
        className="rounded-2xl border border-slate-200 bg-white p-4"
        dangerouslySetInnerHTML={{ __html: svgMarkup }}
      />
      <div className="mt-4 flex gap-2">
        <Button size="sm" onClick={downloadPng} data-testid="badge-download-png" className="rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white">
          <FileImage className="w-4 h-4 mr-1" /> Download PNG
        </Button>
        <Button size="sm" variant="outline" onClick={downloadSvg} data-testid="badge-download-svg" className="rounded-full">
          <Download className="w-4 h-4 mr-1" /> SVG
        </Button>
      </div>
    </div>
  );
}

function escapeXml(s) {
  return String(s || "").replace(/[<>&"']/g, (c) => ({ "<":"&lt;",">":"&gt;","&":"&amp;","\"":"&quot;","'":"&apos;" }[c]));
}
function sanitize(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
function triggerDownload(url, filename) {
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
}
